import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	discardPresentationDraft,
	getInterviewRecordings,
	importInterviewTranscript,
	getPresentationDraftSource,
	getPresentationDrafts,
	publishPresentationDraft,
	regeneratePresentationDraft,
	retryPresentationDraft,
	startPresentationDraft,
	updatePresentationDraftContent
} from "../api/presentation-drafts-api";
import {
	InterviewRecording,
	PresentationDraft,
	PresentationDraftSource,
	StartPresentationDraftRequest
} from "../models/PresentationDraft";
import { useSnackbarContext } from "../contexts/useSnackBarContext";
import { GENERATION_POLL_INTERVAL_MS, isGenerating } from "../utils/presentationDraft";

const draftsKey = (freelancerProfileId: number) => ["presentationDrafts", freelancerProfileId];

export const usePresentationDrafts = (freelancerProfileId: number, isEnabled: boolean = true) =>
	useQuery<PresentationDraft[], Error>({
		queryKey: draftsKey(freelancerProfileId),
		queryFn: () => getPresentationDrafts(freelancerProfileId),
		enabled: isEnabled && freelancerProfileId > 0,
		refetchOnWindowFocus: false,
		refetchInterval: query => (isGenerating(query.state.data) ? GENERATION_POLL_INTERVAL_MS : false)
	});

export const usePresentationDraftSource = (draftId: number, isEnabled: boolean) =>
	useQuery<PresentationDraftSource, Error>({
		queryKey: ["presentationDraftSource", draftId],
		queryFn: () => getPresentationDraftSource(draftId),
		enabled: isEnabled && draftId > 0,
		refetchOnWindowFocus: false,
		staleTime: Infinity
	});

export const useInterviewRecordings = (freelancerProfileId: number, isEnabled: boolean) =>
	useQuery<InterviewRecording[], Error>({
		queryKey: ["interviewRecordings", freelancerProfileId],
		queryFn: () => getInterviewRecordings(freelancerProfileId),
		enabled: isEnabled && freelancerProfileId > 0,
		refetchOnWindowFocus: false,
		staleTime: 5 * 60 * 1000,
		retry: false
	});

export const useImportInterviewTranscript = () =>
	useMutation({
		mutationFn: ({ freelancerProfileId, recordingId }: { freelancerProfileId: number; recordingId: string }) =>
			importInterviewTranscript(freelancerProfileId, recordingId)
	});

const useDraftsInvalidation = () => {
	const queryClient = useQueryClient();

	return (freelancerProfileId: number) =>
		queryClient.invalidateQueries({ queryKey: draftsKey(freelancerProfileId) });
};

export const useStartPresentationDraft = () => {
	const invalidateDrafts = useDraftsInvalidation();
	const { showError } = useSnackbarContext();

	return useMutation({
		mutationFn: (request: StartPresentationDraftRequest) => startPresentationDraft(request),
		onSuccess: draft => invalidateDrafts(draft.freelancerProfileId),
		onError: () => showError("presentationDraft.errors.start")
	});
};

export const useUpdatePresentationDraftContent = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ draftId, content }: { draftId: number; content: string }) =>
			updatePresentationDraftContent(draftId, content),
		onSuccess: saved =>
			queryClient.setQueryData<PresentationDraft[]>(draftsKey(saved.freelancerProfileId), drafts =>
				drafts?.map(draft => (draft.id === saved.id ? { ...saved, version: draft.version } : draft))
			)
	});
};

export const useRegeneratePresentationDraft = () => {
	const invalidateDrafts = useDraftsInvalidation();
	const { showError } = useSnackbarContext();

	return useMutation({
		mutationFn: ({ draftId, instruction }: { draftId: number; instruction: string }) =>
			regeneratePresentationDraft(draftId, instruction),
		onSuccess: draft => invalidateDrafts(draft.freelancerProfileId),
		onError: () => showError("presentationDraft.errors.regenerate")
	});
};

export const useRetryPresentationDraft = () => {
	const invalidateDrafts = useDraftsInvalidation();
	const { showError } = useSnackbarContext();

	return useMutation({
		mutationFn: (draftId: number) => retryPresentationDraft(draftId),
		onSuccess: draft => invalidateDrafts(draft.freelancerProfileId),
		onError: () => showError("presentationDraft.errors.retry")
	});
};

export const usePublishPresentationDraft = () => {
	const queryClient = useQueryClient();
	const invalidateDrafts = useDraftsInvalidation();
	const { showSuccess, showError } = useSnackbarContext();

	return useMutation({
		mutationFn: ({ draftId, isExemplary }: { draftId: number; isExemplary: boolean }) =>
			publishPresentationDraft(draftId, isExemplary),
		onSuccess: draft => {
			invalidateDrafts(draft.freelancerProfileId);
			queryClient.invalidateQueries({ queryKey: ["freelancerProfile"] });
			queryClient.invalidateQueries({ queryKey: ["entityTranslations", "FreelancerProfile", draft.freelancerProfileId] });
			queryClient.invalidateQueries({ queryKey: ["entityTranslationFlags", "FreelancerProfile", draft.freelancerProfileId] });
			showSuccess("presentationDraft.publish.success");
		},
		onError: () => showError("presentationDraft.errors.publish")
	});
};

export const useDiscardPresentationDraft = () => {
	const invalidateDrafts = useDraftsInvalidation();
	const { showError } = useSnackbarContext();

	return useMutation({
		mutationFn: (draftId: number) => discardPresentationDraft(draftId),
		onSuccess: draft => invalidateDrafts(draft.freelancerProfileId),
		onError: () => showError("presentationDraft.errors.discard")
	});
};
